import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import FileDownloadDoneIcon from "@mui/icons-material/FileDownloadDone";
import SyncIcon from "@mui/icons-material/Sync";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import { useGetBranches } from "src/api/generic";
import {
  useImportFoodicsMenuEntities,
  useFoodicsMenuEntities,
  useImportFoodicsSuppliers,
  useFoodicsSuppliers,
} from "src/api/integrations/foodics";
import type {
  FoodicsInventoryItemEntity,
  FoodicsMenuCategoryEntity,
  FoodicsMenuProductEntity,
  FoodicsModifierOptionEntity,
  FoodicsSupplierEntity,
  ImportFoodicsMenuEntitiesBody,
} from "src/types/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";

type ImportType = "categories" | "products" | "both";

type FoodicsImportProductBody = NonNullable<
  ImportFoodicsMenuEntitiesBody["products"]
>[number];

function mapFoodicsProductToImportPayload(
  p: FoodicsMenuProductEntity
): FoodicsImportProductBody {
  return {
    id: p.external_id,
    name: p.name,
    sku: p.sku?.trim() || undefined,
    price: p.price ?? null,
    ...(p.cost != null && String(p.cost).trim() !== ""
      ? { cost: Number(p.cost) }
      : {}),
    type: p.type ?? null,
    category: p.category
      ? {
          id: p.category.id,
          title: p.category.title,
          ...(p.category.sku?.trim()
            ? { sku: p.category.sku.trim() }
            : {}),
        }
      : null,
  };
}

export type FoodicsImportMode = "menu" | "inventory";

type ImportWorkspaceProps = {
  mode: FoodicsImportMode;
};

export const ImportWorkspace = ({ mode }: ImportWorkspaceProps) => {
  const isMenu = mode === "menu";
  const isInventory = mode === "inventory";
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [importType, setImportType] = useState<ImportType>("both");
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [productsPage, setProductsPage] = useState(0);
  const [productsPerPage, setProductsPerPage] = useState(25);
  const [categoriesPage, setCategoriesPage] = useState(0);
  const [categoriesPerPage, setCategoriesPerPage] = useState(100);
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryPerPage, setInventoryPerPage] = useState(100);
  const [modifierOptionsPage, setModifierOptionsPage] = useState(0);
  const [modifierOptionsPerPage, setModifierOptionsPerPage] = useState(50);
  const [selectedModifierOptionIds, setSelectedModifierOptionIds] = useState<
    string[]
  >([]);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState<string[]>(
    []
  );
  const [selectedRecipeProductIds, setSelectedRecipeProductIds] = useState<
    string[]
  >([]);
  const [recipeBranchId, setRecipeBranchId] = useState<string>("");

  const { data, isLoading, refetch } = useFoodicsMenuEntities({
    products_page: productsPage + 1,
    products_per_page: productsPerPage,
    categories_page: categoriesPage + 1,
    categories_per_page: categoriesPerPage,
    include_inventory: isInventory ? 1 : 0,
    include_product_ingredients: isMenu ? 1 : 0,
    include_modifier_options: isMenu ? 1 : 0,
    modifier_options_page: modifierOptionsPage + 1,
    modifier_options_per_page: modifierOptionsPerPage,
    inventory_page: inventoryPage + 1,
    inventory_per_page: inventoryPerPage,
  });
  const { data: branchesResponse } = useGetBranches();
  const { mutate: importItems, status: importStatus } =
    useImportFoodicsMenuEntities();
  const { mutate: importSuppliers, status: importSuppliersStatus } =
    useImportFoodicsSuppliers();
  const [suppliersPage, setSuppliersPage] = useState(0);
  const [suppliersPerPage, setSuppliersPerPage] = useState(50);
  const {
    data: suppliersResponse,
    isLoading: suppliersLoading,
    refetch: refetchSuppliers,
  } = useFoodicsSuppliers(
    { page: suppliersPage + 1, per_page: suppliersPerPage },
    isInventory
  );

  const currentLang = i18n.language || "en";
  const categories = data?.data?.categories ?? [];
  const products = data?.data?.products ?? [];
  const productsPagination = data?.data?.products_pagination;
  const categoriesPagination = data?.data?.categories_pagination;
  const modifierOptions = data?.data?.modifier_options ?? [];
  const modifierOptionsPagination = data?.data?.modifier_options_pagination;
  const modifierOptionsFetchFailed =
    data?.data?.modifier_options_fetch_failed === true;
  const inventoryItems = data?.data?.inventory_items ?? [];
  const inventoryPagination = data?.data?.inventory_pagination;
  const inventoryFetchFailed = data?.data?.inventory_fetch_failed === true;
  const branchesList = branchesResponse?.data ?? [];
  const suppliers = suppliersResponse?.data?.suppliers ?? [];
  const suppliersPagination = suppliersResponse?.data?.pagination ?? null;

  useEffect(() => {
    setSelectedCategoryIds([]);
    setSelectedProductIds([]);
  }, [importType]);

  useEffect(() => {
    if (importStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("menuImport.importSuccess"),
        },
      ]);
      refetch();
      setSelectedCategoryIds([]);
      setSelectedProductIds([]);
      setSelectedInventoryIds([]);
      setSelectedRecipeProductIds([]);
      setSelectedModifierOptionIds([]);
    } else if (importStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("menuImport.importError"),
        },
      ]);
    }
  }, [importStatus]);

  useEffect(() => {
    if (importSuppliersStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("menuImport.importSuccess"),
        },
      ]);
      refetchSuppliers();
    } else if (importSuppliersStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("menuImport.importError"),
        },
      ]);
    }
  }, [importSuppliersStatus]);

  const getLocalized = (text?: { en?: string; ar?: string }): string =>
    text?.[currentLang as "en" | "ar"] || text?.en || text?.ar || "-";

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleProduct = (id: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleInventory = (id: string | null) => {
    if (!id) return;
    setSelectedInventoryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleRecipeProduct = (id: string) => {
    setSelectedRecipeProductIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleModifierOption = (id: string) => {
    setSelectedModifierOptionIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const canImport =
    (isMenu &&
      ((importType === "categories" && selectedCategoryIds.length > 0) ||
        (importType === "products" && selectedProductIds.length > 0) ||
        (importType === "both" &&
          (selectedCategoryIds.length > 0 || selectedProductIds.length > 0)) ||
        selectedRecipeProductIds.length > 0 ||
        selectedModifierOptionIds.length > 0)) ||
    (isInventory && selectedInventoryIds.length > 0);

  const selectableInventoryRows = inventoryItems.filter(
    (r) => !r.already_imported && r.external_id
  );
  const allVisibleInventorySelected =
    selectableInventoryRows.length > 0 &&
    selectableInventoryRows.every((row) =>
      selectedInventoryIds.includes(row.external_id as string)
    );

  const toggleSelectAllInventory = () => {
    if (allVisibleInventorySelected) {
      setSelectedInventoryIds([]);
      return;
    }
    setSelectedInventoryIds(
      selectableInventoryRows
        .map((row) => row.external_id)
        .filter((id): id is string => Boolean(id))
    );
  };

  const allVisibleCategoriesSelected =
    categories.filter((c) => !c.already_imported).length > 0 &&
    categories
      .filter((c) => !c.already_imported)
      .every((category) =>
        selectedCategoryIds.includes(category.external_id)
      );

  const allVisibleProductsSelected =
    products.filter((p) => !p.already_imported).length > 0 &&
    products
      .filter((p) => !p.already_imported)
      .every((product) => selectedProductIds.includes(product.external_id));

  const selectableModifierRows = modifierOptions.filter(
    (r) => !r.already_imported && r.external_id
  );
  const allVisibleModifierOptionsSelected =
    selectableModifierRows.length > 0 &&
    selectableModifierRows.every((row) =>
      selectedModifierOptionIds.includes(row.external_id as string)
    );

  const toggleSelectAllModifierOptions = () => {
    if (allVisibleModifierOptionsSelected) {
      setSelectedModifierOptionIds([]);
      return;
    }
    setSelectedModifierOptionIds(
      selectableModifierRows
        .map((row) => row.external_id)
        .filter((id): id is string => Boolean(id))
    );
  };

  const toggleSelectAllCategories = () => {
    if (allVisibleCategoriesSelected) {
      setSelectedCategoryIds([]);
      return;
    }
    setSelectedCategoryIds(
      categories
        .filter((category) => !category.already_imported)
        .map((category) => category.external_id)
    );
  };

  const toggleSelectAllProducts = () => {
    if (allVisibleProductsSelected) {
      setSelectedProductIds([]);
      return;
    }
    setSelectedProductIds(
      products
        .filter((product) => !product.already_imported)
        .map((product) => product.external_id)
    );
  };

  const visibleCategories = categories;
  const visibleProducts = products;

  const buildInventoryPayload = () =>
    inventoryItems
      .filter(
        (row) =>
          row.external_id &&
          !row.already_imported &&
          selectedInventoryIds.includes(row.external_id)
      )
      .map((row: FoodicsInventoryItemEntity) => ({
        id: row.external_id as string,
        sku: row.sku,
        is_product: row.is_product,
        cost: row.cost ?? undefined,
        name: row.name,
        ingredient_unit:
          typeof row.ingredient_unit === "string"
            ? row.ingredient_unit
            : undefined,
        storage_unit:
          typeof row.storage_unit === "string"
            ? row.storage_unit
            : undefined,
        ingredients: Array.isArray(row.ingredients) ? row.ingredients : [],
        ...(row.minimum_level != null && row.minimum_level !== ""
          ? { minimum_level: row.minimum_level }
          : {}),
        ...(row.maximum_level != null && row.maximum_level !== ""
          ? { maximum_level: row.maximum_level }
          : {}),
        ...(row.supplier?.id
          ? { supplier: { id: row.supplier.id, name: row.supplier.name } }
          : {}),
        ...(row.suppliers?.length
          ? {
              suppliers: row.suppliers.map((s) => ({
                id: s.id,
                name: s.name,
              })),
            }
          : {}),
        ...(row.supplier_id ? { supplier_id: row.supplier_id } : {}),
      }));

  const onImport = () => {
    if (isInventory) {
      const inventoryPayload = buildInventoryPayload();
      if (inventoryPayload.length === 0) {
        return;
      }
      importItems({
        categories: [],
        products: [],
        inventory_items: inventoryPayload,
      });
      return;
    }

    const categoriesPayload =
      importType === "categories" || importType === "both"
        ? visibleCategories
            .filter(
              (c) =>
                !c.already_imported &&
                selectedCategoryIds.includes(c.external_id)
            )
            .map((c: FoodicsMenuCategoryEntity) => ({
              id: c.external_id,
              name: c.name,
              ...(c.sku?.trim() ? { sku: c.sku.trim() } : {}),
            }))
        : [];

    const productsPayload =
      importType === "products" || importType === "both"
        ? visibleProducts
            .filter(
              (p) =>
                !p.already_imported &&
                selectedProductIds.includes(p.external_id)
            )
            .map((p) => mapFoodicsProductToImportPayload(p))
        : [];

    const recipeIds = products
      .filter((p) => selectedRecipeProductIds.includes(p.external_id))
      .filter((p) => (p.ingredient_count ?? 0) > 0)
      .map((p) => p.external_id);

    const modifierOptionsPayload = modifierOptions
      .filter(
        (
          row
        ): row is FoodicsModifierOptionEntity & { external_id: string } =>
          Boolean(row.external_id) &&
          !row.already_imported &&
          selectedModifierOptionIds.includes(row.external_id as string)
      )
      .map((row) => ({
        id: row.external_id,
        name: row.name,
        sku: row.sku?.trim() || undefined,
        price: row.price ?? null,
      }));

    importItems({
      categories: categoriesPayload,
      products: productsPayload,
      ...(modifierOptionsPayload.length > 0
        ? { modifier_options: modifierOptionsPayload }
        : {}),
      ...(recipeIds.length > 0 ? { recipe_product_ids: recipeIds } : {}),
      ...(recipeBranchId
        ? { branch_id: parseInt(recipeBranchId, 10) }
        : {}),
    });
  };

  const importSingleCategory = (category: FoodicsMenuCategoryEntity) => {
    importItems({
      categories: [
        {
          id: category.external_id,
          name: category.name,
          ...(category.sku?.trim() ? { sku: category.sku.trim() } : {}),
        },
      ],
      products: [],
    });
  };

  const importSingleProduct = (product: FoodicsMenuProductEntity) => {
    importItems({
      categories: [],
      products: [mapFoodicsProductToImportPayload(product)],
    });
  };

  const importSingleModifierOption = (row: FoodicsModifierOptionEntity) => {
    if (!row.external_id) return;
    importItems({
      categories: [],
      products: [],
      modifier_options: [
        {
          id: row.external_id,
          name: row.name,
          sku: row.sku?.trim() || undefined,
          price: row.price ?? null,
        },
      ],
    });
  };

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/integrations/foodics")}
            sx={{ color: "#667085" }}
          >
            {generalT("back")}
          </Button>
          <Box>
            <Typography fontSize={24} fontWeight={600} color="#101828">
              {isMenu ? t("foodicsHub.menuPageTitle") : t("foodicsHub.inventoryPageTitle")}
            </Typography>
            <Typography fontSize={14} color="#667085">
              {isMenu
                ? t("foodicsHub.menuPageSubtitle")
                : t("foodicsHub.inventoryPageSubtitle")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => refetch()}>
            {t("actions.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={
              importStatus === "pending" ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadDoneIcon />
              )
            }
            disabled={!canImport || importStatus === "pending"}
            onClick={onImport}
          >
            {isInventory
              ? t("menuImport.importInventory")
              : t("menuImport.importItems")}
          </Button>
        </Stack>
      </Box>

      <Box bgcolor="#F2F4F7" width="100%" height="1px" />

      {isMenu && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            flexWrap="wrap"
          >
            <FormControl size="small" sx={{ maxWidth: 260 }}>
              <InputLabel>{t("menuImport.typeLabel")}</InputLabel>
              <Select
                value={importType}
                label={t("menuImport.typeLabel")}
                onChange={(e) => setImportType(e.target.value as ImportType)}
              >
                <MenuItem value="both">{t("menuImport.types.both")}</MenuItem>
                <MenuItem value="categories">
                  {t("menuImport.types.categories")}
                </MenuItem>
                <MenuItem value="products">
                  {t("menuImport.types.products")}
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220, maxWidth: 320 }}>
              <InputLabel>{t("menuImport.recipeBranchLabel")}</InputLabel>
              <Select
                value={recipeBranchId}
                label={t("menuImport.recipeBranchLabel")}
                onChange={(e) => setRecipeBranchId(e.target.value)}
                displayEmpty
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {branchesList.map((b) => (
                  <MenuItem key={b.id} value={String(b.id)}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography fontSize={12} color="#667085">
            {t("menuImport.syncRecipeHint")}
          </Typography>
        </>
      )}

      {isMenu && (importType === "categories" || importType === "both") && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("menuImport.categories")}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={toggleSelectAllCategories}
              disabled={
                categories.filter((c) => !c.already_imported).length === 0
              }
            >
              {allVisibleCategoriesSelected
                ? t("menuImport.unselectAll")
                : t("menuImport.selectAll")}
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("menuImport.table.select")}</TableCell>
                  <TableCell>{t("menuImport.table.externalId")}</TableCell>
                  <TableCell>{t("menuImport.table.name")}</TableCell>
                  <TableCell>{t("menuImport.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton rowsLength={5} cellsLength={4} />
              ) : (
                <TableBody>
                  {visibleCategories.map((category) => (
                    <TableRow key={category.external_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedCategoryIds.includes(
                            category.external_id
                          )}
                          onChange={() =>
                            toggleCategory(category.external_id)
                          }
                          disabled={category.already_imported}
                        />
                      </TableCell>
                      <TableCell>{category.external_id}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography fontSize={14}>
                            {getLocalized(category.name)}
                          </Typography>
                          {category.already_imported && (
                            <Chip
                              size="small"
                              color="success"
                              label={t("menuImport.imported")}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={
                            category.already_imported ||
                            importStatus === "pending"
                          }
                          onClick={() => importSingleCategory(category)}
                        >
                          <FileDownloadDoneIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!isLoading && categories.length === 0 && <NoData />}
          </TableContainer>
          <TablePagination
            component="div"
            labelRowsPerPage={generalT("rowsPerPage")}
            count={
              categoriesPagination?.total ?? visibleCategories.length
            }
            page={Math.max(
              (categoriesPagination?.current ?? categoriesPage + 1) - 1,
              0
            )}
            onPageChange={(_, newPage) => setCategoriesPage(newPage)}
            rowsPerPage={categoriesPagination?.per ?? categoriesPerPage}
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              setCategoriesPerPage(value);
              setCategoriesPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {isMenu && (importType === "products" || importType === "both") && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Box>
              <Typography fontSize={16} fontWeight={600} color="#101828">
                {t("menuImport.products")}
              </Typography>
              <Typography fontSize={12} color="#667085">
                {t("menuImport.recipeSection")}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={toggleSelectAllProducts}
              disabled={
                products.filter((p) => !p.already_imported).length === 0
              }
            >
              {allVisibleProductsSelected
                ? t("menuImport.unselectAll")
                : t("menuImport.selectAll")}
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("menuImport.table.select")}</TableCell>
                  <TableCell>{t("menuImport.table.externalId")}</TableCell>
                  <TableCell>{t("menuImport.table.sku")}</TableCell>
                  <TableCell>{t("menuImport.table.name")}</TableCell>
                  <TableCell>{t("menuImport.table.category")}</TableCell>
                  <TableCell>{t("menuImport.table.price")}</TableCell>
                  <TableCell>{t("menuImport.table.ingredients")}</TableCell>
                  <TableCell>{t("menuImport.table.recipe")}</TableCell>
                  <TableCell>{t("menuImport.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton rowsLength={5} cellsLength={9} />
              ) : (
                <TableBody>
                  {visibleProducts.map((product) => (
                    <TableRow key={product.external_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProductIds.includes(
                            product.external_id
                          )}
                          onChange={() =>
                            toggleProduct(product.external_id)
                          }
                          disabled={product.already_imported}
                        />
                      </TableCell>
                      <TableCell>{product.external_id}</TableCell>
                      <TableCell>{product.sku || "—"}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography fontSize={14}>
                            {getLocalized(product.name)}
                          </Typography>
                          {product.already_imported && (
                            <Chip
                              size="small"
                              color="success"
                              label={t("menuImport.imported")}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {getLocalized(product.category?.title)}
                      </TableCell>
                      <TableCell>{product.price ?? "-"}</TableCell>
                      <TableCell>{product.ingredient_count ?? 0}</TableCell>
                      <TableCell>
                        <Checkbox
                          size="small"
                          checked={selectedRecipeProductIds.includes(
                            product.external_id
                          )}
                          onChange={() =>
                            toggleRecipeProduct(product.external_id)
                          }
                          disabled={
                            (product.ingredient_count ?? 0) === 0 ||
                            importStatus === "pending"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={
                            product.already_imported ||
                            importStatus === "pending"
                          }
                          onClick={() => importSingleProduct(product)}
                        >
                          <FileDownloadDoneIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!isLoading && products.length === 0 && <NoData />}
          </TableContainer>
          <TablePagination
            component="div"
            labelRowsPerPage={generalT("rowsPerPage")}
            count={productsPagination?.total ?? visibleProducts.length}
            page={Math.max(
              (productsPagination?.current ?? productsPage + 1) - 1,
              0
            )}
            onPageChange={(_, newPage) => setProductsPage(newPage)}
            rowsPerPage={productsPagination?.per ?? productsPerPage}
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              setProductsPerPage(value);
              setProductsPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}

      {isMenu && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
          >
            <Box>
              <Typography fontSize={16} fontWeight={600} color="#101828">
                {t("menuImport.modifierOptions")}
              </Typography>
              <Typography fontSize={12} color="#667085">
                {t("menuImport.modifierOptionsHint")}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="text"
              onClick={toggleSelectAllModifierOptions}
              disabled={selectableModifierRows.length === 0}
            >
              {allVisibleModifierOptionsSelected
                ? t("menuImport.unselectAll")
                : t("menuImport.selectAll")}
            </Button>
          </Box>
          {modifierOptionsFetchFailed && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {t("menuImport.modifierOptionsFetchFailed")}
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("menuImport.table.select")}</TableCell>
                  <TableCell>{t("menuImport.table.externalId")}</TableCell>
                  <TableCell>{t("menuImport.table.sku")}</TableCell>
                  <TableCell>{t("menuImport.table.name")}</TableCell>
                  <TableCell>{t("menuImport.table.modifierGroup")}</TableCell>
                  <TableCell>{t("menuImport.table.price")}</TableCell>
                  <TableCell>{t("menuImport.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableLoadingSkeleton rowsLength={5} cellsLength={7} />
              ) : (
                <TableBody>
                  {modifierOptions.map((row) => (
                    <TableRow key={row.external_id || row.sku || "mod"}>
                      <TableCell>
                        <Checkbox
                          checked={
                            !!row.external_id &&
                            selectedModifierOptionIds.includes(row.external_id)
                          }
                          onChange={() =>
                            row.external_id &&
                            toggleModifierOption(row.external_id)
                          }
                          disabled={row.already_imported || !row.external_id}
                        />
                      </TableCell>
                      <TableCell>{row.external_id || "—"}</TableCell>
                      <TableCell>{row.sku || "—"}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                        >
                          <Typography fontSize={14}>
                            {getLocalized(row.name)}
                          </Typography>
                          {row.already_imported && (
                            <Chip
                              size="small"
                              color="success"
                              label={t("menuImport.imported")}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {getLocalized(row.modifier?.title)}
                      </TableCell>
                      <TableCell>{row.price ?? "-"}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={
                            row.already_imported ||
                            !row.external_id ||
                            importStatus === "pending"
                          }
                          onClick={() => importSingleModifierOption(row)}
                        >
                          <FileDownloadDoneIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!isLoading && modifierOptions.length === 0 && <NoData />}
          </TableContainer>
          <TablePagination
            component="div"
            labelRowsPerPage={generalT("rowsPerPage")}
            count={
              modifierOptionsPagination?.total ?? modifierOptions.length
            }
            page={Math.max(
              (modifierOptionsPagination?.current ??
                modifierOptionsPage + 1) - 1,
              0
            )}
            onPageChange={(_, newPage) => setModifierOptionsPage(newPage)}
            rowsPerPage={
              modifierOptionsPagination?.per ?? modifierOptionsPerPage
            }
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              setModifierOptionsPerPage(value);
              setModifierOptionsPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Box>
      )}

      {isInventory && (
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <Box>
            <Typography fontSize={16} fontWeight={600} color="#101828">
              {t("menuImport.inventorySection")}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t("menuImport.inventoryListHint")}
              {typeof inventoryPagination?.total === "number"
                ? ` ${t("menuImport.inventoryTotalInFoodics", {
                    count: inventoryPagination.total,
                  })}`
                : ""}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="text"
            onClick={toggleSelectAllInventory}
            disabled={selectableInventoryRows.length === 0}
          >
            {allVisibleInventorySelected
              ? t("menuImport.unselectAll")
              : t("menuImport.selectAll")}
          </Button>
        </Box>
        {inventoryFetchFailed && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {t("menuImport.inventoryFetchFailed")}
          </Alert>
        )}
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("menuImport.table.select")}</TableCell>
                <TableCell>{t("menuImport.table.externalId")}</TableCell>
                <TableCell>{t("menuImport.table.sku")}</TableCell>
                <TableCell>{t("menuImport.table.name")}</TableCell>
                <TableCell>{t("menuImport.table.kind")}</TableCell>
                <TableCell>{t("menuImport.table.ingredients")}</TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <TableLoadingSkeleton rowsLength={5} cellsLength={6} />
            ) : (
              <TableBody>
                {inventoryItems.map((row, index) => (
                  <TableRow
                    key={row.external_id ?? `inv-row-${index}-${row.sku}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={
                          !!row.external_id &&
                          selectedInventoryIds.includes(row.external_id)
                        }
                        onChange={() => toggleInventory(row.external_id)}
                        disabled={row.already_imported || !row.external_id}
                      />
                    </TableCell>
                    <TableCell>{row.external_id ?? "—"}</TableCell>
                    <TableCell>{row.sku || "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontSize={14}>
                          {getLocalized(row.name)}
                        </Typography>
                        {row.already_imported && (
                          <Chip
                            size="small"
                            color="success"
                            label={t("menuImport.imported")}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          row.is_product
                            ? t("menuImport.kindProduction")
                            : t("menuImport.kindRaw")
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{row.ingredient_count ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        {!isLoading && inventoryItems.length === 0 && <NoData />}
        <TablePagination
          component="div"
          labelRowsPerPage={generalT("rowsPerPage")}
          count={inventoryPagination?.total ?? inventoryItems.length}
          page={Math.max(
            (inventoryPagination?.current ?? inventoryPage + 1) - 1,
            0
          )}
          onPageChange={(_, newPage) => setInventoryPage(newPage)}
          rowsPerPage={inventoryPagination?.per ?? inventoryPerPage}
          onRowsPerPageChange={(event) => {
            const value = parseInt(event.target.value, 10);
            setInventoryPerPage(value);
            setInventoryPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Box>
      )}

      {isInventory && (
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography fontSize={16} fontWeight={600} color="#101828">
                {t("foodicsHub.suppliersCardTitle")}
              </Typography>
              <Typography fontSize={12} color="#667085">
                {t("foodicsHub.suppliersComingSoon")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={() => refetchSuppliers()}>
                {t("actions.refresh")}
              </Button>
              <Button
                variant="contained"
                onClick={() => importSuppliers()}
                disabled={importSuppliersStatus === "pending"}
                startIcon={
                  importSuppliersStatus === "pending" ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )
                }
              >
                {t("actions.syncSuppliers")}
              </Button>
            </Stack>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("menuImport.table.externalId")}</TableCell>
                  <TableCell>{t("menuImport.table.name")}</TableCell>
                  <TableCell>{generalT("email")}</TableCell>
                  <TableCell>{t("menuImport.table.phone")}</TableCell>
                  <TableCell>{t("menuImport.table.taxNumber")}</TableCell>
                </TableRow>
              </TableHead>
              {suppliersLoading ? (
                <TableLoadingSkeleton rowsLength={6} cellsLength={5} />
              ) : (
                <TableBody>
                  {suppliers.map((s: FoodicsSupplierEntity, idx: number) => (
                    <TableRow key={s.external_id ?? s.name ?? `sup-${idx}`}>
                      <TableCell>{s.external_id ?? "—"}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography fontSize={14}>{s.name ?? "—"}</Typography>
                          {s.already_imported && (
                            <Chip
                              size="small"
                              color="success"
                              label={t("menuImport.imported")}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{s.email ?? "—"}</TableCell>
                      <TableCell>{s.phone ?? "—"}</TableCell>
                      <TableCell>{s.supplier_tax_number ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {!suppliersLoading && suppliers.length === 0 && <NoData />}
          </TableContainer>

          <TablePagination
            component="div"
            labelRowsPerPage={generalT("rowsPerPage")}
            count={suppliersPagination?.total ?? suppliers.length}
            page={Math.max((suppliersPagination?.current ?? suppliersPage + 1) - 1, 0)}
            onPageChange={(_, newPage) => setSuppliersPage(newPage)}
            rowsPerPage={suppliersPagination?.per ?? suppliersPerPage}
            onRowsPerPageChange={(event) => {
              const value = parseInt(event.target.value, 10);
              setSuppliersPerPage(value);
              setSuppliersPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Box>
      )}
    </Stack>
  );
};
